import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiExternalLink, FiFilm } = FiIcons;

const getPlaybackUrl = (item) => {
  const playbacks = item?.playbacks || [];
  const preferred = playbacks.find((playback) => {
    const name = String(playback?.name || '').toLowerCase();
    const url = String(playback?.url || '').toLowerCase();
    return name.includes('mp4') || name.includes('avc') || url.includes('.mp4');
  });

  return preferred?.url || playbacks[0]?.url || null;
};

const getThumbnail = (item) => {
  const cuts = item?.image?.cuts || item?.photo?.cuts || [];
  return cuts.find((cut) => Number(cut.width) >= 600)?.src ||
    cuts[0]?.src ||
    item?.image?.src ||
    null;
};

const getMlbUrl = (item, playbackUrl) => {
  if (item?.url) return item.url;
  if (item?.link) return item.link;
  if (item?.slug) return `https://www.mlb.com/video/${item.slug}`;
  return playbackUrl;
};

const VideoCard = ({ item, index }) => {
  const title = item.title || item.headline || 'Video Highlight';
  const description = item.description || item.blurb || '';
  const playbackUrl = getPlaybackUrl(item);
  const thumbnail = getThumbnail(item);
  const externalUrl = getMlbUrl(item, playbackUrl);

  return (
    <motion.article
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      className="overflow-hidden border border-[#658d71] bg-[rgba(0,0,0,0.1)]"
    >
      <div className="aspect-video bg-black">
        {playbackUrl ? (
          <video
            src={playbackUrl}
            poster={thumbnail || undefined}
            controls
            playsInline
            preload="metadata"
            aria-label={title}
            className="h-full w-full object-contain"
          />
        ) : thumbnail ? (
          <img
            src={thumbnail}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <SafeIcon icon={FiFilm} size={30} className="text-[#658d71]" />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 p-3">
        <h3 className="text-[11px] font-black uppercase leading-tight tracking-wide text-white">
          {title}
        </h3>
        {description && (
          <p className="line-clamp-2 text-[9px] leading-relaxed text-[#a9c9b1]">
            {description}
          </p>
        )}
        {externalUrl && (
          <a
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 self-start text-[9px] font-bold uppercase tracking-widest text-[#d8ceb3] hover:text-white"
          >
            Open on MLB
            <SafeIcon icon={FiExternalLink} size={10} />
          </a>
        )}
      </div>
    </motion.article>
  );
};

const RecapVideos = ({ content }) => {
  const videos = useMemo(() => {
    const items = content?.highlights?.highlights?.items || [];
    return items.filter(
      (item) => item.type === 'video' || item.playbacks?.length > 0
    );
  }, [content]);

  if (!videos.length) {
    return (
      <div className="py-10 text-center text-xs uppercase tracking-widest text-[#658d71]">
        No video highlights available for this game
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[9px] font-bold uppercase tracking-widest text-[#658d71]">
        {videos.length} highlight{videos.length === 1 ? '' : 's'} available
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {videos.map((item, index) => (
          <VideoCard key={item.id || index} item={item} index={index} />
        ))}
      </div>
    </div>
  );
};

export default RecapVideos;